import React, { useState, useMemo } from 'react';
import {
  FiFilter as Filter, FiDatabase as Database, FiChevronDown as ChevronDown, FiChevronRight as ChevronRight,
  FiX as X, FiExternalLink as ExternalLink, FiCalendar as Calendar, FiSearch as Search,
  FiDownload as Download, FiArrowUp as SortAsc, FiArrowDown as SortDesc, FiCopy as Copy,
  FiCheck as Check
} from 'react-icons/fi';
import { getUtilizationLevel, getUtilizationColorClasses } from '../../../../../hooks/useMemoryAlerts';
import { COLUMN_TOOLTIPS } from './memoryTooltips';
import HelpTooltip from '../../../../../components/common/HelpTooltip';

interface MemorySnapshot {
  id?: string;
  memory_type?: string;
  operation?: string;
  token_count?: number;
  max_tokens?: number;
  run_id?: string;
  timestamp?: string;
  content_json?: unknown;
}

interface MemoryFilters {
  memoryType: string;
  operation: string;
  runId: string;
  [key: string]: string;
}

interface Props {
  snapshots: MemorySnapshot[] | null | undefined;
  loading: boolean;
  filters: MemoryFilters;
  updateFilters: (updates: Partial<MemoryFilters>) => void;
  clearFilters: () => void;
  currentPage: number;
  totalPages: number;
  goToPage: (page: number) => void;
  onNavigateToRun: (runId: string) => void;
}

// Color constants
const MEMORY_TYPE_COLORS: Record<string, string> = {
  working: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
  conversation: 'bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-300',
  tool_cache: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
};

const OPERATION_COLORS: Record<string, string> = {
  read: 'bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300',
  write: 'bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300',
  update: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-800 dark:text-yellow-300',
  prune: 'bg-red-100 dark:bg-red-900/50 text-red-800 dark:text-red-300',
};

const getMemoryTypeColor = (type: string | undefined): string =>
  MEMORY_TYPE_COLORS[type?.toLowerCase() ?? ''] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';

const getOperationColor = (operation: string | undefined): string =>
  OPERATION_COLORS[operation?.toLowerCase() ?? ''] ?? 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';

interface SkeletonProps {
  className?: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ className }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700 rounded ${className}`} />
);

const getAnomalyBorder = (snapshot: MemorySnapshot): string => {
  if (!snapshot.token_count || !snapshot.max_tokens) {
    if (snapshot.operation?.toLowerCase() === 'prune') return 'border-l-4 border-l-orange-500';
    return '';
  }
  const util = (snapshot.token_count / snapshot.max_tokens) * 100;
  if (util >= 90) return 'border-l-4 border-l-red-500';
  if (util >= 70) return 'border-l-4 border-l-yellow-500';
  if (snapshot.operation?.toLowerCase() === 'prune') return 'border-l-4 border-l-orange-500';
  return '';
};

interface SmartContentPreviewProps {
  content: unknown;
  searchTerm: string;
}

interface Message {
  role: string;
  content: unknown;
}

interface ParsedContent {
  messages?: Message[];
  [key: string]: unknown;
}

const SmartContentPreview: React.FC<SmartContentPreviewProps> = ({ content, searchTerm }) => {
  const [expanded, setExpanded] = useState(false);

  const parsedContent = useMemo((): ParsedContent | Message[] | null => {
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      return parsed as ParsedContent | Message[];
    } catch {
      return null;
    }
  }, [content]);

  const isConversation = useMemo((): boolean => {
    if (!parsedContent) return false;
    if (Array.isArray(parsedContent)) {
      return parsedContent.some((item: unknown) => {
        const msg = item as Message;
        return msg.role && msg.content;
      });
    }
    const obj = parsedContent;
    if (obj.messages && Array.isArray(obj.messages)) {
      return obj.messages.some(item => item.role && item.content);
    }
    return false;
  }, [parsedContent]);

  const messages = useMemo((): Message[] => {
    if (!isConversation) return [];
    if (Array.isArray(parsedContent)) {
      return parsedContent.filter(m => m.role && m.content);
    }
    const obj = parsedContent;
    return ((obj?.messages) ?? []).filter(m => m.role && m.content);
  }, [isConversation, parsedContent]);

  const formattedContent = useMemo((): string => {
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      return JSON.stringify(parsed, null, 2);
    } catch {
      return typeof content === 'string' ? content : JSON.stringify(content);
    }
  }, [content]);

  const highlightSearch = (text: string): React.ReactNode => {
    if (!searchTerm || typeof text !== 'string') return text;
    const parts = text.split(new RegExp(`(${searchTerm.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
    return parts.map((part, i) =>
      part.toLowerCase() === searchTerm.toLowerCase()
        ? <mark key={`hl-${part}-${i}`} className="bg-yellow-200 dark:bg-yellow-900">{part}</mark>
        : part
    );
  };

  if (isConversation && messages.length > 0) {
    const displayMessages = expanded ? messages : messages.slice(0, 4);
    return (
      <div className="space-y-2">
        {displayMessages.map((msg, i) => {
          const isUser = ['user', 'human'].includes(msg.role);
          const isSystem = msg.role === 'system';
          const msgText = typeof msg.content === 'string'
            ? msg.content
            : JSON.stringify(msg.content);
          return (
            <div key={`msg-${msg.role}-${i}`} className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-xs ${
                isSystem
                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 italic'
                  : isUser
                    ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
              }`}>
                <span className="font-semibold text-[10px] uppercase opacity-60 block mb-0.5">{msg.role}</span>
                <span className="whitespace-pre-wrap">
                  {highlightSearch(msgText.slice(0, 300))}
                  {msgText.length > 300 && '...'}
                </span>
              </div>
            </div>
          );
        })}
        {messages.length > 4 && (
          <button onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
            {expanded ? 'Show less' : `Show all ${messages.length} messages`}
          </button>
        )}
      </div>
    );
  }

  const displayContent = expanded ? formattedContent : formattedContent.slice(0, 500);
  const isLong = formattedContent.length > 500;

  return (
    <div className="relative">
      <pre className="text-xs text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono overflow-x-auto">
        {searchTerm ? highlightSearch(displayContent) : displayContent}
        {!expanded && isLong && '...'}
      </pre>
      {isLong && (
        <button onClick={() => setExpanded(!expanded)}
          className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:underline">
          {expanded ? 'Show less' : `Show more (${formattedContent.length.toLocaleString()} chars)`}
        </button>
      )}
    </div>
  );
};

interface ExpandedRowContentProps {
  snapshot: MemorySnapshot;
  onNavigateToRun: (runId: string) => void;
  searchTerm: string;
}

const ExpandedRowContent: React.FC<ExpandedRowContentProps> = ({ snapshot, onNavigateToRun, searchTerm }) => {
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const handleCopy = (text: string, field: string): void => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const utilization = snapshot.max_tokens
    ? (snapshot.token_count! / snapshot.max_tokens) * 100
    : 0;
  const { level } = getUtilizationLevel(snapshot.token_count, snapshot.max_tokens);
  const colors = getUtilizationColorClasses(level);

  return (
    <tr className="bg-gray-50 dark:bg-gray-900/50">
      <td colSpan={8} className="px-6 py-4">
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Snapshot ID</p>
              <div className="flex items-center">
                <p className="text-sm font-mono text-gray-900 dark:text-white truncate mr-2">
                  {snapshot.id ?? 'N/A'}
                </p>
                {snapshot.id && (
                  <button onClick={() => { if (snapshot.id) handleCopy(snapshot.id, 'id'); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    {copiedField === 'id' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                  </button>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Run ID</p>
              <div className="flex items-center">
                <button onClick={() => snapshot.run_id && onNavigateToRun(snapshot.run_id)}
                  className={`text-sm font-mono truncate mr-2 ${snapshot.run_id ? 'text-blue-600 dark:text-blue-400 hover:underline cursor-pointer' : 'text-gray-400 dark:text-gray-500 cursor-default'}`}>
                  {snapshot.run_id ?? 'N/A'}
                </button>
                {snapshot.run_id && (
                  <button onClick={() => { if (snapshot.run_id) handleCopy(snapshot.run_id, 'run_id'); }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                    {copiedField === 'run_id' ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                  </button>
                )}
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Context Utilization</p>
              <div className="flex items-center mt-1">
                <div className="w-20 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mr-2">
                  <div className={`h-full ${colors.bg}`}
                    style={{ width: `${Math.min(utilization, 100)}%` }} />
                </div>
                <span className={`text-sm font-medium ${colors.text}`}>
                  {utilization.toFixed(1)}%
                </span>
              </div>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400">Timestamp</p>
              <p className="text-sm text-gray-900 dark:text-white">
                {snapshot.timestamp ? new Date(snapshot.timestamp).toLocaleString() : 'N/A'}
              </p>
            </div>
          </div>

          {snapshot.content_json != null && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Content</p>
                <button onClick={() => handleCopy(
                  typeof snapshot.content_json === 'string'
                    ? snapshot.content_json
                    : JSON.stringify(snapshot.content_json, null, 2),
                  'content'
                )} className="flex items-center text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  {copiedField === 'content' ? (
                    <><Check className="w-3 h-3 mr-1 text-green-500" /> Copied</>
                  ) : (
                    <><Copy className="w-3 h-3 mr-1" /> Copy</>
                  )}
                </button>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 max-h-64 overflow-auto">
                <SmartContentPreview content={snapshot.content_json} searchTerm={searchTerm} />
              </div>
            </div>
          )}

          {snapshot.run_id && (
            <div className="flex items-center space-x-4">
              <button onClick={() => { if (snapshot.run_id) onNavigateToRun(snapshot.run_id); }}
                className="flex items-center text-sm text-blue-600 dark:text-blue-400 hover:underline">
                <ExternalLink className="w-4 h-4 mr-1" />
                View Agent Run
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

interface ColumnHeaderProps {
  label: string;
  tooltipKey?: string;
  sortKey?: string;
  sortConfig?: SortConfig;
  onSort?: (key: string) => void;
}

const ColumnHeader: React.FC<ColumnHeaderProps> = ({ label, tooltipKey, sortKey, sortConfig, onSort }) => {
  const isSortable = !!sortKey;

  return (
    <th
      className={`px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
        isSortable ? 'cursor-pointer hover:text-gray-700 dark:hover:text-gray-300' : ''
      }`}
      onClick={isSortable && onSort ? () => onSort(sortKey) : undefined}
    >
      <div className="flex items-center space-x-1">
        <span>{label}</span>
        {tooltipKey && COLUMN_TOOLTIPS[tooltipKey] && (
          <HelpTooltip text={COLUMN_TOOLTIPS[tooltipKey]} />
        )}
        {isSortable && sortConfig && (
          sortConfig.key === sortKey
            ? sortConfig.direction === 'asc'
              ? <SortAsc className="w-3 h-3 text-blue-600" />
              : <SortDesc className="w-3 h-3 text-blue-600" />
            : <SortAsc className="w-3 h-3 text-gray-400" />
        )}
      </div>
    </th>
  );
};

interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  goToPage: (page: number) => void;
}

const PaginationControls: React.FC<PaginationControlsProps> = ({ currentPage, totalPages, goToPage }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-2 mt-6">
      <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}
        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        Previous
      </button>
      <span className="text-gray-700 dark:text-gray-300">Page {currentPage} of {totalPages}</span>
      <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}
        className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
        Next
      </button>
    </div>
  );
};

type ExportFormat = null | 'json' | 'csv' | 'menu';

const MemoryDetailsTab: React.FC<Props> = ({
  snapshots,
  loading,
  filters,
  updateFilters,
  clearFilters,
  currentPage,
  totalPages,
  goToPage,
  onNavigateToRun,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'timestamp', direction: 'desc' });
  const [showFilters, setShowFilters] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat>(null);

  const filterOptions = useMemo(() => {
    const memoryTypes = new Set<string>();
    const operations = new Set<string>();
    snapshots?.forEach(s => {
      if (s.memory_type) memoryTypes.add(s.memory_type);
      if (s.operation) operations.add(s.operation);
    });
    return { memoryTypes: Array.from(memoryTypes), operations: Array.from(operations) };
  }, [snapshots]);

  const processedSnapshots = useMemo((): MemorySnapshot[] => {
    let result = [...(snapshots ?? [])];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s =>
        s.memory_type?.toLowerCase().includes(term) ||
        s.operation?.toLowerCase().includes(term) ||
        s.run_id?.toLowerCase().includes(term) ||
        (typeof s.content_json === 'string' && s.content_json.toLowerCase().includes(term))
      );
    }

    result.sort((a, b) => {
      let aVal: number | string | undefined = (a as Record<string, unknown>)[sortConfig.key] as string | number | undefined;
      let bVal: number | string | undefined = (b as Record<string, unknown>)[sortConfig.key] as string | number | undefined;
      if (sortConfig.key === 'timestamp') {
        aVal = new Date(aVal as string).getTime();
        bVal = new Date(bVal as string).getTime();
      }
      if ((aVal ?? '') < (bVal ?? '')) return sortConfig.direction === 'asc' ? -1 : 1;
      if ((aVal ?? '') > (bVal ?? '')) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [snapshots, searchTerm, sortConfig]);

  const toggleRowExpand = (id: string | number): void => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (key: string): void => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const hasActiveFilters = filters.memoryType || filters.operation || filters.runId;

  const formatNumber = (num: number | undefined | null): string => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  const formatTimestamp = (timestamp: string | undefined): string => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const handleExport = (format: 'json' | 'csv'): void => {
    setExportFormat(null);
    const data = processedSnapshots.map(s => ({
      id: s.id,
      memory_type: s.memory_type,
      operation: s.operation,
      token_count: s.token_count,
      max_tokens: s.max_tokens,
      utilization: s.max_tokens ? ((s.token_count! / s.max_tokens) * 100).toFixed(1) + '%' : 'N/A',
      run_id: s.run_id,
      timestamp: s.timestamp,
    }));

    let blob: Blob;
    let filename: string;

    if (format === 'csv') {
      const headers = Object.keys(data[0] ?? {}).join(',');
      const rows = data.map(row => Object.values(row).map(v => `"${String(v ?? '').replaceAll('"', '""')}"`).join(','));
      blob = new Blob([headers + '\n' + rows.join('\n')], { type: 'text/csv' });
      filename = `memory-snapshots-${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      filename = `memory-snapshots-${new Date().toISOString().split('T')[0]}.json`;
    }

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        {[1, 2, 3, 4, 5].map(i => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }

  if (!snapshots || snapshots.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <Database className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
          No memory snapshots found
        </h3>
        <p className="text-gray-500 dark:text-gray-400 text-center max-w-md">
          Memory snapshots will appear here once agents start executing with memory operations.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center space-x-4 flex-1">
          {/* Search */}
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search snapshots..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {searchTerm && (
              <button onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Filter */}
          <div className="relative">
            <button onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${
                hasActiveFilters
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400'
                  : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
              } transition-colors`}>
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filter</span>
            </button>

            {showFilters && (
              <div className="absolute top-full mt-2 right-0 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 min-w-[240px]">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Filters</span>
                  {hasActiveFilters && (
                    <button onClick={() => { clearFilters(); setShowFilters(false); }}
                      className="text-xs text-blue-600 dark:text-blue-400 hover:underline">Clear all</button>
                  )}
                </div>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="memory-type" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Memory Type</label>
                    <select id="memory-type" value={filters.memoryType} onChange={(e) => updateFilters({ memoryType: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                      <option value="">All Types</option>
                      {filterOptions.memoryTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="operation" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Operation</label>
                    <select id="operation" value={filters.operation} onChange={(e) => updateFilters({ operation: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm">
                      <option value="">All Operations</option>
                      {filterOptions.operations.map(op => (
                        <option key={op} value={op}>{op}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button onClick={() => setShowFilters(false)}
                  className="mt-4 w-full px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Apply</button>
              </div>
            )}
          </div>
        </div>

        {/* Export Dropdown */}
        <div className="relative">
          <button onClick={() => setExportFormat(exportFormat ? null : 'menu')}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <Download className="w-4 h-4" />
            <span className="text-sm">Export</span>
            <ChevronDown className="w-3 h-3" />
          </button>
          {exportFormat === 'menu' && (
            <div className="absolute top-full mt-1 right-0 z-20 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden min-w-[120px]">
              <button onClick={() => handleExport('json')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                JSON
              </button>
              <button onClick={() => handleExport('csv')}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                CSV
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Active Filters Pills */}
      {hasActiveFilters && (
        <div className="flex items-center flex-wrap gap-2">
          {filters.memoryType && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300">
              Type: {filters.memoryType}
              <button onClick={() => updateFilters({ memoryType: '' })} className="ml-2"><X className="w-3 h-3" /></button>
            </span>
          )}
          {filters.operation && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300">
              Operation: {filters.operation}
              <button onClick={() => updateFilters({ operation: '' })} className="ml-2"><X className="w-3 h-3" /></button>
            </span>
          )}
        </div>
      )}

      {/* Search Results Count */}
      {searchTerm && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Found {processedSnapshots.length} result{processedSnapshots.length === 1 ? '' : 's'} for &ldquo;{searchTerm}&rdquo;
        </p>
      )}

      {/* Anomaly legend */}
      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 border-l-2 border-l-red-500" /> &gt;90% utilization
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 border-l-2 border-l-yellow-500" /> &gt;70% utilization
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 border-l-2 border-l-orange-500" /> Prune operation
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/50">
            <tr>
              <th className="w-10 px-4 py-3"></th>
              <ColumnHeader label="Memory Type" tooltipKey="memory_type" sortKey="memory_type" sortConfig={sortConfig} onSort={handleSort} />
              <ColumnHeader label="Token Count" tooltipKey="token_count" sortKey="token_count" sortConfig={sortConfig} onSort={handleSort} />
              <ColumnHeader label="Max Tokens" tooltipKey="max_tokens" />
              <ColumnHeader label="Utilization" tooltipKey="utilization" />
              <ColumnHeader label="Operation" tooltipKey="operation" sortKey="operation" sortConfig={sortConfig} onSort={handleSort} />
              <ColumnHeader label="Run ID" tooltipKey="run_id" />
              <ColumnHeader label="Timestamp" tooltipKey="timestamp" sortKey="timestamp" sortConfig={sortConfig} onSort={handleSort} />
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {processedSnapshots.map((snapshot, index) => {
              const rowKey = snapshot.id ?? index;
              const isExpanded = expandedRows.has(rowKey);
              const anomalyBorder = getAnomalyBorder(snapshot);
              const utilization = snapshot.max_tokens
                ? (snapshot.token_count! / snapshot.max_tokens) * 100
                : 0;
              const { level } = getUtilizationLevel(snapshot.token_count, snapshot.max_tokens);
              const utilizationColors = getUtilizationColorClasses(level);

              return (
                <React.Fragment key={rowKey}>
                  <tr
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer ${
                      isExpanded ? 'bg-gray-50 dark:bg-gray-700/30' : ''
                    } ${anomalyBorder}`}
                    onClick={() => toggleRowExpand(rowKey)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleRowExpand(rowKey); }} role="row" tabIndex={0}
                  >
                    <td className="px-4 py-4">
                      {isExpanded
                        ? <ChevronDown className="w-4 h-4 text-gray-400" />
                        : <ChevronRight className="w-4 h-4 text-gray-400" />
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getMemoryTypeColor(snapshot.memory_type)}`}>
                        {snapshot.memory_type ?? 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {formatNumber(snapshot.token_count ?? 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-gray-300">
                        {snapshot.max_tokens ? formatNumber(snapshot.max_tokens) : 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {snapshot.max_tokens ? (
                        <div className="flex items-center">
                          <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mr-2">
                            <div className={`h-full ${utilizationColors.bg}`}
                              style={{ width: `${Math.min(utilization, 100)}%` }} />
                          </div>
                          <span className={`text-xs font-medium ${utilizationColors.text}`}>
                            {utilization.toFixed(0)}%
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getOperationColor(snapshot.operation)}`}>
                        {snapshot.operation ?? 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {snapshot.run_id ? (
                        <button
                          onClick={(e) => { e.stopPropagation(); if (snapshot.run_id) onNavigateToRun(snapshot.run_id); }}
                          className="text-sm text-blue-600 dark:text-blue-400 font-mono hover:underline">
                          {String(snapshot.run_id).slice(0, 8)}...
                        </button>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500 font-mono">N/A</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1 text-sm text-gray-900 dark:text-gray-300">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        {formatTimestamp(snapshot.timestamp)}
                      </div>
                    </td>
                  </tr>
                  {isExpanded && (
                    <ExpandedRowContent
                      snapshot={snapshot}
                      onNavigateToRun={onNavigateToRun}
                      searchTerm={searchTerm}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <PaginationControls currentPage={currentPage} totalPages={totalPages} goToPage={goToPage} />
    </div>
  );
};

export default MemoryDetailsTab;
